import { useState } from "react";
import { Link } from "react-router-dom";
import { HardHat, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, TRADES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const HERO = "https://images.unsplash.com/photo-1563166423-482a8c14b2d6?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

export default function AuthPage() {
  const { signIn } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", trade: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !form.trade) return toast.error("Choisissez votre métier");
    setBusy(true);
    try {
      const body = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(`/auth/${mode}`, body);
      signIn(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const isReg = mode === "register";
  return (
    <div className="min-h-screen md:grid md:grid-cols-2 bg-white">
      <div className="relative h-56 md:h-auto">
        <img src={HERO} alt="Chantier" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="relative h-full flex flex-col justify-end p-6 md:p-12 text-white">
          <span className="w-12 h-12 rounded-lg bg-orange-500 grid place-items-center mb-4">
            <HardHat className="w-7 h-7 text-slate-900" />
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase leading-none">
            Le français<br />du chantier
          </h1>
          <p className="mt-3 text-base text-slate-200">5 minutes par jour. Des mots utiles.</p>
        </div>
        <div className="hazard-stripe h-2 absolute bottom-0 inset-x-0" />
      </div>

      <form onSubmit={submit} className="p-6 md:p-12 flex flex-col justify-center max-w-md w-full mx-auto space-y-5 rise" data-testid="auth-form">
        <h2 className="font-display text-3xl font-bold uppercase">{isReg ? "Créer mon profil" : "Connexion"}</h2>
        {isReg && (
          <>
            <div className="space-y-2">
              <Label htmlFor="name">Prénom</Label>
              <Input id="name" className="h-12 text-base" value={form.name} onChange={set("name")} required data-testid="auth-firstname-input" />
            </div>
            <div className="space-y-2">
              <Label>Métier</Label>
              <Select value={form.trade} onValueChange={(v) => setForm({ ...form, trade: v })}>
                <SelectTrigger className="h-12 text-base" data-testid="auth-trade-select">
                  <SelectValue placeholder="Choisir mon métier" />
                </SelectTrigger>
                <SelectContent>
                  {TRADES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`auth-trade-option-${t}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        <div className="space-y-2">
          <Label htmlFor="email">{isReg ? "Email" : "Email ou identifiant"}</Label>
          <Input id="email" type={isReg ? "email" : "text"} autoCapitalize="none" className="h-12 text-base" value={form.email} onChange={set("email")} required data-testid="auth-email-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Mot de passe</Label>
          <Input id="password" type="password" minLength={6} className="h-12 text-base" value={form.password} onChange={set("password")} required data-testid="auth-password-input" />
        </div>
        {!isReg && (
          <Link to="/mot-de-passe-oublie" data-testid="auth-forgot-link" className="-mt-2 text-sm font-semibold text-orange-600 hover:underline self-end">
            Mot de passe oublié ?
          </Link>
        )}
        <button
          type="submit"
          disabled={busy}
          data-testid="auth-submit-btn"
          className="h-14 rounded-xl bg-orange-500 hover:bg-orange-600 active:scale-[.98] text-white font-display text-xl font-bold uppercase transition-[background-color,transform] flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {busy && <Loader2 className="w-5 h-5 animate-spin" />}
          {isReg ? "C'est parti" : "Entrer"}
        </button>
        <button
          type="button"
          onClick={() => setMode(isReg ? "login" : "register")}
          data-testid="auth-toggle-mode-btn"
          className="text-sm font-semibold text-slate-600 hover:text-orange-600 transition-colors"
        >
          {isReg ? "J'ai déjà un profil → Connexion" : "Nouveau ? → Créer mon profil"}
        </button>
        <Link to="/verifier" data-testid="auth-verify-link" className="text-xs font-semibold text-slate-500 hover:text-orange-600 text-center">
          Employeur ? Vérifier un certificat
        </Link>
      </form>
    </div>
  );
}
